import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Image,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Link,
} from "@chakra-ui/react";
import { fetchUserDevices } from "../services/api";
import AddProductDialog from "./AddProductDialog";
import { getProductImageFromStructure } from '../utils/getProductImageFromStructure';
import { productStructure } from '../services/productStructure';

const RegisteredProducts = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);

  useEffect(() => {
    const loadDevices = async () => {
      setLoading(true);
      try {
        const userDevices = await fetchUserDevices();
        setDevices(userDevices);
        setError(null);
      } catch (err) {
        console.error("Failed to load devices:", err);
        setError(
          "Failed to load your registered products. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    loadDevices();
  }, []);

  const handleAddProduct = (device) => {
    // TODO: Implement product registration logic
    console.log("Adding product:", device);
    setIsAddProductOpen(false);
  };

  const handleUnknownClick = (device) => {
    setIsAddProductOpen(true);
  };

  return (
    <VStack spacing={6} align="stretch">
      <Heading as="h1" size="xl">
        Your Registered Products
      </Heading>
      <Text>
        View and manage all your registered Calibrite products. From here, you
        can see detailed information about each device, update settings, and
        manage reminders.
      </Text>

      {/* Products Listing */}
      <Box borderWidth="1px" borderRadius="lg" p={4} borderColor="gray.300">
        <Heading as="h2" size="md" mb={3}>
          Product List
        </Heading>
        <Flex justify="space-between" align="center" mb={4}>
          <Text>
            Below is a complete list of all products registered to your account.
          </Text>
          <Button colorScheme="blue" onClick={() => setIsAddProductOpen(true)}>
            Add Product
          </Button>
        </Flex>

        {error && (
          <Alert status="error" mb={4}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        {loading ? (
          <Flex justify="center" align="center" py={10}>
            <Spinner size="xl" />
          </Flex>
        ) : (
          <TableContainer>
            <Table colorScheme="simple">
              <Thead>
                <Tr>
                  <Th>Image</Th>
                  <Th>Product Type</Th>
                  <Th>Product Model</Th>
                  <Th>Serial Number</Th>
                  <Th>Registration Date</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {devices.length > 0 ? (
                  devices.map((device, index) => (
                    <Tr key={index}>
                      <Td>
                        <Image
                          src={getProductImageFromStructure(productStructure, device.productType, device.productModel)}
                          alt={device.productType}
                          boxSize="50px"
                          objectFit="contain"
                          fallbackSrc="https://via.placeholder.com/50"
                        />
                      </Td>
                      <Td>{device.productType}</Td>
                      <Td>{device.productModel}</Td>
                      <Td>
                        {device.needsRegistration ? (
                          <>{device.serialNumber}</>
                        ) : (
                          device.serialNumber
                        )}
                      </Td>
                      <Td>{device.registrationDate || "N/A"}</Td>
                      <Td>
                        <Link
                          color="blue.500"
                          onClick={() => handleUnknownClick(device)}
                          cursor="pointer"
                        >
                          <Badge
                            colorScheme={
                              device.isRegistered && device.optedIn
                                ? "green"
                                : "gray"
                            }
                          >
                            {device.isRegistered && device.optedIn
                              ? "Registered"
                              : "Un-registered"}
                          </Badge>{" "}
                        </Link>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={4}>
                      <Text>No products registered yet.</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Registration Benefits */}
      <Box borderWidth="1px" borderRadius="lg" p={4} borderColor="gray.300">
        <Heading as="h2" size="md" mb={3}>
          Benefits of Registering
        </Heading>
        <VStack align="start" spacing={3}>
          <Text>
            <strong>✓ Extended warranty</strong> - Get additional coverage for
            your products
          </Text>
          <Text>
            <strong>✓ Calibration reminders</strong> - Never miss an important
            calibration
          </Text>
          <Text>
            <strong>✓ Software updates</strong> - Stay up to date with the
            latest features
          </Text>
          <Text>
            <strong>✓ Exclusive offers</strong> - Access to special promotions
            for registered users
          </Text>
        </VStack>
      </Box>

      {/* Add Product Dialog */}
      <AddProductDialog
        isOpen={isAddProductOpen}
        onClose={() => {
          setIsAddProductOpen(false);
        }}
        onAddProduct={handleAddProduct}
      />
    </VStack>
  );
};

export default RegisteredProducts;
